/**
 * FIXED: Enhanced with strict real data prioritization
 * CRITICAL: Prevents hallucination by ensuring real API data is used when available
 */
import { DatabaseApiOptions, DatabaseApiResponse } from "../types/database-api-types";
import { searchArbeidsplassen, isJobQuery, isExplicitArbeidsplassenQuery } from "./arbeidsplassen-client-service";
import { processBrregQuery } from "./brreg-service";
import { detectITQuery } from "../utils/industry-detection";
import { supabase } from '@/integrations/supabase/client';

// Import NEW strict Utdanning.no detection
import { isExplicitUtdanningQuery, isStrictEducationQuery } from "./utdanning-explicit-checker";

// Import existing services for database detection
import { detectDatabaseFromQuery } from "../utils/database-detection";

/**
 * Check if query mentions Enhetsregisteret with absolute priority
 */
function hasAbsoluteEnhetsregisteretPriority(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  console.log(`[Database-API] ABSOLUTE PRIORITY CHECK: Analyzing query: "${query}"`);
  
  // Comprehensive list of Enhetsregisteret variations
  const enhetsregisterVariations = [
    'enhetsregisteret', 'enhetsregister', 'enhets-registeret', 'enhets-register',
    'brønnøysundregisteret', 'brønnøysund-registeret', 'brønnøysund registeret',
    'brønnøysund', 'brreg', 'br reg', 'br-reg',
    'søk enhetsregisteret', 'søke enhetsregisteret', 'finn i enhetsregisteret',
    'kan du søke enhetsregisteret', 'søk i enhetsregisteret'
  ];
  
  const hasEnhetsregister = enhetsregisterVariations.some(variation => lowerQuery.includes(variation));
  
  if (hasEnhetsregister) {
    console.log(`[Database-API] 🏛️ ABSOLUTE PRIORITY: ENHETSREGISTERET detected - routing directly to BRREG`);
    return true;
  }
  
  return false;
}

/**
 * Check if query is business-related for BRREG
 */
function isBusinessQuery(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  
  const businessKeywords = [
    'bedrifter', 'firmaer', 'firma', 'selskap', 'as', 'asa',
    'butikker', 'butikk', 'forretninger', 'verksted', 'verksteder',
    'byggfirma', 'byggfirmaer', 'snekkerfirma', 'rørleggerfirma',
    'it-firmaer', 'it-firma', 'it-selskap', 'teknologifirma',
    'frisører', 'frisør', 'salong', 'hårsalong'
  ];
  
  return businessKeywords.some(keyword => lowerQuery.includes(keyword));
}

/**
 * CRITICAL: Validate that response contains real data, not mock data
 */
function validateRealData(response: DatabaseApiResponse): { isReal: boolean; reason: string } {
  console.log(`[Database-API] 🔍 VALIDATING DATA AUTHENTICITY`);
  
  // Check for explicit mock indicators
  if (response.isMockData === true) {
    console.log(`[Database-API] ❌ MOCK DATA DETECTED: isMockData flag is true`);
    return { isReal: false, reason: "isMockData flag is true" };
  }
  
  if (response.mockReason) {
    console.log(`[Database-API] ❌ MOCK DATA DETECTED: Has mockReason: ${response.mockReason}`);
    return { isReal: false, reason: `mockReason: ${response.mockReason}` };
  }
  
  // Check formatted response for mock indicators
  if (response.formattedResponse) {
    const mockIndicators = [
      '[MOCK DATA]',
      'MOCK/FALLBACK',
      'testdata',
      'mock-data',
      'IKKE reelle',
      'IKKE ekte',
      'ikke gyldige',
      'API-kallet feilet',
      'fallback-data',
      'Dette er testdata'
    ];
    
    const hasMockIndicator = mockIndicators.some(indicator => 
      response.formattedResponse!.toLowerCase().includes(indicator.toLowerCase())
    );
    
    if (hasMockIndicator) {
      console.log(`[Database-API] ❌ MOCK DATA DETECTED: Found mock indicator in response`);
      return { isReal: false, reason: "Mock indicator found in formatted response" };
    }
  }
  
  // Check metadata for mock indicators
  if (response.result?.metadata) {
    const metadata = response.result.metadata;
    if (metadata.source?.includes('MOCK') || 
        metadata.source?.includes('fallback') ||
        metadata.realApiDataUsed === false ||
        metadata.isMockData === true) {
      console.log(`[Database-API] ❌ MOCK DATA DETECTED: Metadata indicates mock data`);
      return { isReal: false, reason: "Metadata indicates mock data" };
    }
  }
  
  console.log(`[Database-API] ✅ REAL DATA VALIDATED: No mock indicators found`);
  return { isReal: true, reason: "No mock indicators detected" };
}

/**
 * ENHANCED SYSTEM: Main function with real data prioritization and context preservation
 */
export const callDatabaseApi = async (options: DatabaseApiOptions): Promise<DatabaseApiResponse> => {
  const { query, maxResults = 20, parameters = {} } = options;
  
  // CRITICAL FIX: Use original query for routing decisions if available
  const routingQuery = parameters?.originalQuery || query;
  const actualSearchQuery = query;
  
  console.log(`[Database-API] 🎯 ANTI-HALLUCINATION SYSTEM: Processing query: "${actualSearchQuery}"`);
  if (parameters?.originalQuery) {
    console.log(`[Database-API] 📋 Using original context for routing: "${routingQuery}"`);
  }
  console.log(`[Database-API] Options:`, { maxResults, database: options.database });
  
  try {
    // ABSOLUTE PRIORITY 1: Any mention of Enhetsregisteret goes directly to BRREG
    if (hasAbsoluteEnhetsregisteretPriority(routingQuery)) {
      console.log(`[Database-API] ABSOLUTE PRIORITY 1: Enhetsregisteret detected in routing context - routing to BRREG without any other checks`);
      const response = await callCentralDatabaseApi(options, 'brreg');
      
      // CRITICAL: Validate that we got real data
      const validation = validateRealData(response);
      if (!validation.isReal) {
        console.error(`[Database-API] 🚨 CRITICAL: BRREG returned mock data! Reason: ${validation.reason}`);
        
        // Force error response to prevent hallucination
        return {
          result: {
            data: [],
            metadata: {
              source: 'brreg-validation-failed',
              queryTime: 0,
              queryType: 'validation-error',
              error: `Mock data detected: ${validation.reason}`,
              searchSuccessful: false,
              antiHallucinationCheck: true
            }
          },
          formattedResponse: `## ❌ Feil ved søk i Enhetsregisteret

Systemet detekterte at mock-data ble returnert i stedet for ekte data fra Enhetsregisteret.

**Årsak:** ${validation.reason}

**For å få ekte resultater:**
- Gå direkte til [Enhetsregisteret](https://www.brreg.no/bedriftssok/)
- Søk manuelt etter "${routingQuery}"

---
*🛡️ Anti-hallusinasjonssystem aktivert: Mock-data blokkert*`,
          error: `Anti-hallucination: ${validation.reason}`
        };
      }
      
      console.log(`[Database-API] ✅ REAL DATA CONFIRMED: BRREG returned validated real data`);
      return response;
    }
    
    // PRIORITY 2: Explicit Arbeidsplassen.no mentions - check routing context
    if (isExplicitArbeidsplassenQuery(routingQuery)) {
      console.log(`[Database-API] PRIORITY 2: Explicit Arbeidsplassen.no mention detected in routing context`);
      return await searchArbeidsplassen({ query: actualSearchQuery, maxResults });
    }
    
    // PRIORITY 3: STRICT EXPLICIT UTDANNING.NO DETECTION - check routing context
    if (isExplicitUtdanningQuery(routingQuery)) {
      console.log(`[Database-API] PRIORITY 3: 🎓 EXPLICIT UTDANNING.NO mention detected in routing context - routing to education API`);
      return await callCentralDatabaseApi(options, 'utdanning');
    }
    
    // PRIORITY 4: Job-related queries (without explicit mentions) - check routing context
    if (isJobQuery(routingQuery)) {
      console.log(`[Database-API] PRIORITY 4: Job query detected in routing context, routing to Arbeidsplassen`);
      return await searchArbeidsplassen({ query: actualSearchQuery, maxResults });
    }
    
    // PRIORITY 5: Business queries - Route to BRREG with validation - check routing context
    if (isBusinessQuery(routingQuery)) {
      console.log(`[Database-API] PRIORITY 5: Business query detected in routing context, routing to BRREG with validation`);
      const response = await callCentralDatabaseApi(options, 'brreg');
      
      // CRITICAL: Validate that we got real data
      const validation = validateRealData(response);
      if (!validation.isReal) {
        console.error(`[Database-API] 🚨 CRITICAL: Business search returned mock data! Reason: ${validation.reason}`);
        
        // Force error response to prevent hallucination
        return {
          result: {
            data: [],
            metadata: {
              source: 'brreg-business-validation-failed',
              queryTime: 0,
              queryType: 'business-validation-error',
              error: `Mock data detected for business query: ${validation.reason}`,
              searchSuccessful: false,
              antiHallucinationCheck: true
            }
          },
          formattedResponse: `## ❌ Feil ved bedriftssøk

Systemet detekterte at mock-data ble returnert i stedet for ekte bedriftsdata.

**Søk:** "${routingQuery}"
**Årsak:** ${validation.reason}

**For å få ekte resultater:**
- Gå direkte til [Enhetsregisteret](https://www.brreg.no/bedriftssok/)
- Søk manuelt etter bedrifter

---
*🛡️ Anti-hallusinasjonssystem: Mock-data forhindret*`,
          error: `Business search anti-hallucination: ${validation.reason}`
        };
      }
      
      console.log(`[Database-API] ✅ REAL BUSINESS DATA CONFIRMED: Validated authentic business data`);
      return response;
    }
    
    // PRIORITY 6: Default fallback - try to detect which database to use from routing context
    console.log(`[Database-API] PRIORITY 6: No clear category, using fallback detection on routing context`);
    
    const detectedDatabase = detectDatabaseFromQuery(routingQuery);
    console.log(`[Database-API] PRIORITY 6: Detected database from routing context: ${detectedDatabase}`);
    
    // Route based on detected database (but education requires explicit mention)
    switch (detectedDatabase) {
      case 'utdanning':
        console.log(`[Database-API] PRIORITY 6: EDUCATION detected but NO EXPLICIT MENTION in routing context - not routing to Utdanning.no`);
        // Fall through to default (BRREG) since education requires explicit mention
        
      case 'arbeidsplassen':
        console.log(`[Database-API] PRIORITY 6: ARBEIDSPLASSEN query detected in routing context`);
        return await searchArbeidsplassen({ query: actualSearchQuery, maxResults });
        
      case 'brreg':
      default:
        console.log(`[Database-API] PRIORITY 6: BRREG query (default) - routing through database-api Edge function with validation`);
        const response = await callCentralDatabaseApi(options, 'brreg');
        
        // CRITICAL: Always validate data in fallback scenarios
        const validation = validateRealData(response);
        if (!validation.isReal) {
          console.error(`[Database-API] 🚨 CRITICAL: Fallback BRREG returned mock data! Reason: ${validation.reason}`);
          
          return {
            result: {
              data: [],
              metadata: {
                source: 'brreg-fallback-validation-failed',
                queryTime: 0,
                queryType: 'fallback-validation-error',
                error: `Fallback mock data detected: ${validation.reason}`,
                searchSuccessful: false,
                antiHallucinationCheck: true
              }
            },
            formattedResponse: `## ❌ Søket kunne ikke utføres

Systemet kunne ikke hente ekte data for søket "${routingQuery}".

**Årsak:** ${validation.reason}

**Alternative løsninger:**
- Reformuler spørringen med mer spesifikke søkeord
- Gå direkte til relevante nettsteder
- Prøv igjen senere

---
*🛡️ Anti-hallusinasjonssystem: Ekte data kreves*`,
            error: `Fallback anti-hallucination: ${validation.reason}`
          };
        }
        
        console.log(`[Database-API] ✅ FALLBACK REAL DATA CONFIRMED: Validated authentic fallback data`);
        return response;
    }
    
  } catch (error) {
    console.error(`[Database-API] 🚨 ANTI-HALLUCINATION SYSTEM ERROR:`, error);
    
    return {
      result: {
        data: [],
        metadata: {
          source: 'database-api-error-anti-hallucination',
          queryTime: 0,
          queryType: 'error-anti-hallucination',
          error: error.message,
          searchSuccessful: false,
          antiHallucinationCheck: true
        }
      },
      formattedResponse: `## ❌ Systemfeil

Det oppstod en feil i anti-hallusinasjonssystemet for søket "${routingQuery}".

**Feilmelding:** ${error.message}

**Anbefalinger:**
- Prøv igjen om noen minutter
- Reformuler spørringen
- Gå direkte til relevante nettsteder

---
*🛡️ Anti-hallusinasjonssystem: Systemfeil*`,
      error: error.message
    };
  }
};

/**
 * Call central database-api Edge function with real data validation
 */
async function callCentralDatabaseApi(options: DatabaseApiOptions, database: string): Promise<DatabaseApiResponse> {
  try {
    console.log(`[Database-API] 🔌 CALLING EDGE FUNCTION: ${database} with anti-hallucination validation`);
    
    const { data, error } = await supabase.functions.invoke('database-api', {
      body: { 
        query: options.query,
        database: database,
        maxResults: options.maxResults || 20,
        parameters: options.parameters || {},
        antiHallucinationMode: true, // Enable strict real data mode
        requireRealData: true // Explicitly require real data
      }
    });
    
    if (error) {
      console.error(`[Database-API] 🚨 EDGE FUNCTION ERROR for ${database}:`, error);
      throw new Error(`Central API error: ${error.message || 'Unknown error'}`);
    }
    
    if (!data) {
      console.error(`[Database-API] 🚨 NO DATA from Edge function for ${database}`);
      throw new Error('No data returned from central API');
    }
    
    console.log(`[Database-API] ✅ EDGE FUNCTION SUCCESS for ${database}:`, {
      success: data.success !== false,
      hasResults: !!data.result,
      database: database,
      antiHallucinationMode: data.result?.metadata?.antiHallucinationMode
    });
    
    return data;
    
  } catch (error) {
    console.error(`[Database-API] 🚨 EDGE FUNCTION FAILED for ${database}:`, error);
    
    return {
      result: {
        data: [],
        metadata: {
          source: `${database}-edge-function-error`,
          queryTime: 0,
          queryType: `${database}-edge-function-error`,
          error: error.message,
          searchSuccessful: false,
          antiHallucinationCheck: true
        }
      },
      formattedResponse: `## ❌ Feil ved tilgang til ${database === 'brreg' ? 'Enhetsregisteret' : database === 'utdanning' ? 'Utdanning.no' : database}

Edge function-kallet for "${options.query}" feilet.

**Feilmelding:** ${error.message}

**Forslag:**
- Prøv igjen senere
- Reformuler spørringen
- Gå direkte til relevant nettsted

---
*🛡️ Anti-hallusinasjonssystem: Edge function feil*`,
      error: error.message
    };
  }
}
